import {
  Icons,
  LabelChip,
  Typography,
  Box,
  Stack,
  tokens,
} from '@cast/design-system';

type SettingRowProps = {
  title: string;
  isNew?: boolean;
  description?: string | string[];
  isDefault?: boolean;
  isSelected?: boolean;
  hasError?: boolean;
  onClick?: () => void;
  testId: string;
  startAdornment?: React.ReactNode;
  actionButton?: React.ReactNode;
  noBorderTop?: boolean;
};

export const SettingRow = ({
  title,
  isNew,
  description,
  isDefault,
  isSelected,
  hasError,
  onClick,
  testId,
  startAdornment,
  actionButton,
  noBorderTop,
}: SettingRowProps) => {
  const titleColor = hasError
    ? tokens.theme.fg.status.error
    : isSelected
    ? tokens.theme.fg.selection.onPriamrySubtle
    : tokens.theme.fg.structural.primary;

  const descriptionColor = hasError
    ? tokens.theme.fg.status.error
    : tokens.theme.fg.structural.secondary;

  return (
    <Box
      display="flex"
      px={12}
      py={8}
      borderTop={noBorderTop ? 'none' : '1px solid'}
      borderColor={tokens.theme.border.structural.neutralSubtle}
      justifyContent="space-between"
      alignItems="center"
      sx={{
        '&:hover': onClick
          ? {
              backgroundColor: isSelected
                ? tokens.theme.bg.selection.primarySubtleHover
                : tokens.theme.bg.action.surfaceHover,
              cursor: 'pointer',
            }
          : undefined,
        backgroundColor: isSelected
          ? tokens.theme.bg.selection.primarySubtle
          : undefined,
      }}
      onClick={onClick}
      data-testid={testId}
    >
      <Stack direction="row" alignItems="center" gap={5}>
        <Box>{startAdornment}</Box>
        <Stack>
          <Stack direction="row" alignItems="center" gap={4}>
            <Typography variant="P12M" sx={{ color: titleColor }}>
              {title}
            </Typography>
            {isNew && (
              <LabelChip color="indigo" size="sm">
                NEW
              </LabelChip>
            )}
          </Stack>
          <Stack gap={8} direction="row">
            <Stack>
              {(Array.isArray(description) ? description : [description]).map(
                (desc, index) => (
                  <Typography
                    variant="P10R"
                    sx={{ color: descriptionColor }}
                    key={index}
                    data-testid="description"
                  >
                    {desc}
                  </Typography>
                )
              )}
            </Stack>
            {isDefault && (
              <Typography
                variant="P10R"
                sx={{ color: tokens.theme.fg.structural.secondary }}
                ml={-4}
                fontStyle="italic"
              >
                (default)
              </Typography>
            )}
          </Stack>
          {actionButton && (
            <Box data-testid="setting-row-action-button" mt={4}>
              {actionButton}
            </Box>
          )}
        </Stack>
      </Stack>
      {isSelected && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{ color: tokens.theme.fg.structural.secondary }}
          data-testid="selected-setting-row-icon"
        >
          <Icons.CaretRight size={20} />
        </Box>
      )}
    </Box>
  );
};
